/**
 * ConfigureSigningSessionModal — per-signer signing plan (Bill 2026-07-08).
 *
 * The FSR sets, per required signer, HOW they sign (in-branch vs remote), the
 * signature CAPTURE primitive (drawn vs typed), and how their disclosures are
 * DELIVERED (email / print & hand / print & mail). The result is a
 * SigningSessionConfig persisted on cart.metadataJson.signingConfig and read by
 * the commit pipeline to branch in-branch vs remote activation.
 *
 * Bill's confirmed decisions (2026-07-08):
 *   1. Drawn is the in-branch capture default; typed is the remote / fallback.
 *   2. Delivery FOLLOWS the mode by default (in-branch → print-hand;
 *      remote → email tokenized link), FSR-overridable per signer. A signer
 *      with NO email can't use email delivery (NetTeller unavailable) — the
 *      fallback is print-mail (remote) / print-hand (in-branch).
 *
 * This modal is opened from SignStep for multi-signer in-branch / mixed carts.
 * Single-in-branch and online carts instant-initiate with a default config and
 * skip it (SignStep owns that branch).
 */
import { useEffect, useState } from 'react';
import { ModalShell, Button } from '@/components/primitives';
import type {
  SigningSessionConfig,
  SigningSignerConfig,
  SigningSignerMode,
  SigningCaptureMode,
  SigningDeliveryMode,
} from '@retail-ops/shared-types';

/**
 * Minimal signer shape — structurally a subset of SignStep's dispatcherSigners
 * (SignerInfo), so the caller passes that array directly.
 */
export interface ConfigureSigningModalSigner {
  signerIndex: number;
  displayName: string;
  firstName?: string;
  lastName?: string;
  /** Email on file — empty string means none (drives the NetTeller/email gate). */
  email: string;
  cifNumber: string;
}

export interface ConfigureSigningSessionModalProps {
  open: boolean;
  signers: ReadonlyArray<ConfigureSigningModalSigner>;
  /** Online/RCP cart → default every signer to remote (else in-branch). */
  isOnlineCart: boolean;
  /** Existing persisted config — re-hydrated when the modal reopens. */
  existingConfig?: SigningSessionConfig | null;
  /** Stamped into configuredBy (officer upn / code). */
  configuredBy?: string;
  /**
   * Signer indexes the SERVER says must sign at least one document (Bill
   * 2026-09-30: the modal opens whenever a signature requirement is
   * triggered, so the officer marks each required signer in person or
   * remote). Signers not in the set receive their documents only — no
   * signature is asked of them, so their row carries delivery alone.
   * `undefined` = unknown (older server) → every signer is treated as required.
   */
  requiredSignerIndexes?: ReadonlySet<number>;
  /** True while the required-signer list is still loading from the server. */
  requiredLoading?: boolean;
  onConfirm: (config: SigningSessionConfig) => void;
  onClose: () => void;
}

// ── Remote-signing gate (Bill 2026-07-08) ──────────────────────────────────
//
// Remote signing — the customer-at-home lifecycle: emailed tokenized link +
// OTP, account posts INACTIVE (Core Director Field 20) until the disclosures
// are signed, activation reconciler, auto-close sweep after N days — is
// Track A (BACKLOG-0848). It is BLOCKED pending confirmation of the Core
// Director AcctMod account-status write field + Inactive code (canonical
// rule #1: DO NOT GUESS Core Director fields) and the 0832 remote-invite
// mechanism. Until that lands, EVERY signer signs IN-BRANCH at the desk
// kiosk — so exposing a live "Remote" method here would be a silent no-op
// (the dispatcher runs the in-branch surface regardless). We therefore gate
// the Remote option OFF. Flip this to true in the SAME commit that ships the
// server remote lifecycle so the Method→Remote option + the online-cart
// instant-remote path light up together.
//
// 2026-07-13 (Item 23) — REMOTE SIGNING FULLY WIRED, flag ON. The server rail
// (POST /v3/signing/remote/invite emailed link + out-of-band access code,
// POST /v3/signing/kiosk/otp challenge, self-drive step advancement,
// activate-on-completion + auto-close sweep) AND the FSR INITIATE UI (SignStep
// sends an invite per remote signer via client.retail.signing.remote.invite
// and surfaces the access code + delivery status in RemoteInvitesPanel) are
// both live, so selecting Method → Remote genuinely sends — no silent no-op.
export const REMOTE_SIGNING_ENABLED = true;

// ── Defaults (Bill 2026-07-08) ──────────────────────────────────────────────

function hasEmail(s: ConfigureSigningModalSigner): boolean {
  return !!s.email && s.email.trim().length > 0;
}

/** Drawn is the in-branch default; typed is the remote/fallback (Decision 1). */
function defaultCaptureFor(mode: SigningSignerMode): SigningCaptureMode {
  return mode === 'in-branch' ? 'drawn' : 'typed';
}

/**
 * CHG-0096 (Bill 2026-10-26): a signer present in the branch always draws (Risk's 7/16 rule; there is no in-branch
 * case for typed). The server refused every typed capture in branch, so a Typed choice here left the customer drawing
 * on a pad the server would never accept. Typed stays a choice for remote signers only.
 */
function captureFor(mode: SigningSignerMode, chosen: SigningCaptureMode): SigningCaptureMode {
  return mode === 'in-branch' ? 'drawn' : chosen;
}

/**
 * Delivery follows the mode (Decision 2). Email is unavailable without an
 * email on file — the fallback is print-mail (remote) / print-hand (in-branch).
 */
function defaultDeliveryFor(mode: SigningSignerMode, emailAvailable: boolean): SigningDeliveryMode {
  if (mode === 'remote') return emailAvailable ? 'email' : 'print-mail';
  return 'print-hand';
}

interface SignerRow {
  signerIndex: number;
  mode: SigningSignerMode;
  capture: SigningCaptureMode;
  delivery: SigningDeliveryMode;
}

function seedRows(
  signers: ReadonlyArray<ConfigureSigningModalSigner>,
  isOnlineCart: boolean,
  existing?: SigningSessionConfig | null,
): SignerRow[] {
  return signers.map((s): SignerRow => {
    const emailAvail = hasEmail(s);
    const prior = existing?.signers.find((c) => c.signerIndex === s.signerIndex);
    if (prior) {
      // Re-hydrate a prior config; clamp a remote mode that's no longer
      // available (gate off), then coerce an email choice that's not valid.
      const mode: SigningSignerMode =
        !REMOTE_SIGNING_ENABLED && prior.mode === 'remote' ? 'in-branch' : prior.mode;
      // A saved in-branch + typed plan (possible before CHG-0096) reads back as drawn.
      const capture = captureFor(mode, mode === prior.mode ? prior.capture : defaultCaptureFor(mode));
      const delivery =
        mode !== prior.mode
          ? defaultDeliveryFor(mode, emailAvail)
          : prior.delivery === 'email' && !emailAvail
            ? defaultDeliveryFor(prior.mode, false)
            : prior.delivery;
      return { signerIndex: s.signerIndex, mode, capture, delivery };
    }
    const mode: SigningSignerMode =
      isOnlineCart && REMOTE_SIGNING_ENABLED ? 'remote' : 'in-branch';
    return {
      signerIndex: s.signerIndex,
      mode,
      capture: defaultCaptureFor(mode),
      delivery: defaultDeliveryFor(mode, emailAvail),
    };
  });
}

// ── Styles (PCB brand via CSS vars — no CDN) ────────────────────────────────

const introS: React.CSSProperties = {
  margin: 0,
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--ink-600)',
};

const cardS: React.CSSProperties = {
  border: '1px solid var(--ink-100)',
  borderRadius: 'var(--r-md, 8px)',
  padding: 'var(--sp-5)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-4)',
  background: 'var(--surface-subtle, #f8fafc)',
};

const nameS: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--pcb-blue-dark)',
};

const subS: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-500, #64748b)',
  marginTop: 2,
};

const fieldsRowS: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-4)',
};

const fieldGroupS: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  minWidth: 150,
  flex: '1 1 150px',
};

const labelS: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: 'var(--ink-500, #64748b)',
};

const selectS: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--ink-200)',
  background: 'var(--surface-card)',
  color: 'var(--ink-900)',
  fontSize: 13,
  fontFamily: 'inherit',
};

const badgeRequiredS: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: 'var(--on-brand)',
  background: 'var(--brand-fill)',
  borderRadius: 999,
  padding: '2px 8px',
};
const badgeInfoS: React.CSSProperties = {
  ...{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3, textTransform: 'uppercase' as const, borderRadius: 999, padding: '2px 8px' },
  color: 'var(--ink-600, #4b5563)',
  background: 'var(--ink-100, #e5e7eb)',
};
const noteS: React.CSSProperties = {
  fontSize: 12,
  lineHeight: 1.4,
  color: 'var(--pcb-red, #b10505)',
  background: 'var(--surface-danger-subtle, #fdecec)',
  border: '1px solid var(--danger-border, #f3b6b6)',
  borderRadius: 'var(--r-sm)',
  padding: '6px 10px',
};

export function ConfigureSigningSessionModal({
  open,
  signers,
  isOnlineCart,
  existingConfig,
  configuredBy,
  requiredSignerIndexes,
  requiredLoading = false,
  onConfirm,
  onClose,
}: ConfigureSigningSessionModalProps): JSX.Element {
  const [rows, setRows] = useState<SignerRow[]>(() => seedRows(signers, isOnlineCart, existingConfig));
  const isRequired = (idx: number): boolean => requiredSignerIndexes === undefined || requiredSignerIndexes.has(idx);
  const requiredCount = signers.filter((s) => isRequired(s.signerIndex)).length;
  // A required signer marked remote must have an email — the link has nowhere to go.
  const remoteWithoutEmail = rows
    .filter((r) => r.mode === 'remote' && isRequired(r.signerIndex))
    .map((r) => signers.find((s) => s.signerIndex === r.signerIndex))
    .filter((s): s is ConfigureSigningModalSigner => !!s && !hasEmail(s));

  // Re-seed whenever the modal opens (re-hydrate prior config / fresh signers).
  useEffect(() => {
    if (open) setRows(seedRows(signers, isOnlineCart, existingConfig));
    // Seed only on open transition — mid-edit prop churn must not wipe choices.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const signerByIndex = (idx: number): ConfigureSigningModalSigner | undefined =>
    signers.find((s) => s.signerIndex === idx);

  const setMode = (idx: number, mode: SigningSignerMode): void => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.signerIndex !== idx) return r;
        const emailAvail = hasEmail(signerByIndex(idx) ?? ({ email: '' } as ConfigureSigningModalSigner));
        // Defaults follow the mode; the FSR can re-override capture/delivery after.
        return {
          ...r,
          mode,
          capture: defaultCaptureFor(mode),
          delivery: defaultDeliveryFor(mode, emailAvail),
        };
      }),
    );
  };

  const setCapture = (idx: number, capture: SigningCaptureMode): void => {
    setRows((prev) => prev.map((r) => (r.signerIndex === idx ? { ...r, capture: captureFor(r.mode, capture) } : r)));
  };

  const setDelivery = (idx: number, delivery: SigningDeliveryMode): void => {
    setRows((prev) => prev.map((r) => (r.signerIndex === idx ? { ...r, delivery } : r)));
  };

  const handleConfirm = (): void => {
    const config: SigningSessionConfig = {
      version: 1,
      signers: rows.map((r): SigningSignerConfig => {
        const s = signerByIndex(r.signerIndex);
        return {
          signerIndex: r.signerIndex,
          ...(s?.cifNumber ? { cifId: s.cifNumber } : {}),
          ...(s?.displayName ? { displayName: s.displayName } : {}),
          ...(s && hasEmail(s) ? { email: s.email } : {}),
          mode: r.mode,
          capture: captureFor(r.mode, r.capture),
          delivery: r.delivery,
        };
      }),
      anyRemote: rows.some((r) => r.mode === 'remote'),
      configuredAt: new Date().toISOString(),
      ...(configuredBy ? { configuredBy } : {}),
    };
    onConfirm(config);
  };

  const footer = (
    <>
      <Button variant="ghost" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleConfirm}
        disabled={rows.length === 0 || requiredLoading || remoteWithoutEmail.length > 0}
      >
        {requiredLoading ? 'Checking who must sign…' : 'Start signing session'}
      </Button>
    </>
  );

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Configure signing session"
      size="lg"
      footer={footer}
    >
      <p style={introS}>
        {requiredSignerIndexes !== undefined
          ? `${requiredCount} of ${signers.length} ${signers.length === 1 ? 'person' : 'people'} on this application must sign. `
          : ''}
        Mark who is here. Anyone marked <strong>present</strong> signs now at the desk
        (drawn or typed). Anyone marked <strong>not present</strong> gets a remote signing
        session (emailed secure link plus a one-time code you relay), and you carry a
        follow-up task for each of them until their signature lands. The account opening
        does not wait for them. Everyone else receives their documents only.
        {!REMOTE_SIGNING_ENABLED && (
          <>
            {' '}
            <span style={{ color: 'var(--ink-500, #64748b)' }}>
              Remote signing (emailed secure link) activates in a later release.
            </span>
          </>
        )}
      </p>

      {rows.length === 0 && (
        <p style={{ ...introS, color: 'var(--pcb-red, #b10505)' }}>
          No signers found on this application yet.
        </p>
      )}

      {rows.map((r) => {
        const s = signerByIndex(r.signerIndex);
        const emailAvail = !!s && hasEmail(s);
        const name = s?.displayName || `Signer ${r.signerIndex + 1}`;
        const required = isRequired(r.signerIndex);
        return (
          <div key={r.signerIndex} style={cardS}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <div style={nameS}>{name}</div>
              <span style={required ? badgeRequiredS : badgeInfoS}>
                {requiredLoading ? 'checking…' : required ? 'Signature required' : 'Receives documents only'}
              </span>
              <div style={{ ...subS, marginTop: 0, flexBasis: '100%' }}>
                {emailAvail ? s?.email : 'No email on file'}
                {s?.cifNumber ? ` · CIF ${s.cifNumber}` : ''}
              </div>
            </div>

            <div style={fieldsRowS}>
              {required && (
              <div style={fieldGroupS}>
                <label style={labelS} htmlFor={`mode-${r.signerIndex}`}>
                  Where do they sign?
                </label>
                <select
                  id={`mode-${r.signerIndex}`}
                  style={selectS}
                  value={r.mode}
                  onChange={(e) => setMode(r.signerIndex, e.target.value as SigningSignerMode)}
                >
                  <option value="in-branch">Present — signs here now</option>
                  <option value="remote" disabled={!REMOTE_SIGNING_ENABLED}>
                    Not present — remote signing session{REMOTE_SIGNING_ENABLED ? '' : ' — coming soon'}
                  </option>
                </select>
              </div>
              )}

              {required && (
              <div style={fieldGroupS}>
                <label style={labelS} htmlFor={`capture-${r.signerIndex}`}>
                  Signature
                </label>
                {r.mode === 'in-branch' ? (
                  // CHG-0096: present signers always draw; no choice to make.
                  <div id={`capture-${r.signerIndex}`} style={{ ...subS, marginTop: 6 }}>Drawn on the signature pad</div>
                ) : (
                  <select
                    id={`capture-${r.signerIndex}`}
                    style={selectS}
                    value={r.capture}
                    onChange={(e) => setCapture(r.signerIndex, e.target.value as SigningCaptureMode)}
                  >
                    <option value="drawn">Drawn</option>
                    <option value="typed">Typed</option>
                  </select>
                )}
              </div>
              )}

              <div style={fieldGroupS}>
                <label style={labelS} htmlFor={`delivery-${r.signerIndex}`}>
                  Documents
                </label>
                <select
                  id={`delivery-${r.signerIndex}`}
                  style={selectS}
                  value={r.delivery}
                  onChange={(e) => setDelivery(r.signerIndex, e.target.value as SigningDeliveryMode)}
                >
                  <option value="email" disabled={!emailAvail}>
                    Email{emailAvail ? '' : ' (no email on file)'}
                  </option>
                  <option value="print-hand">Print &amp; hand-deliver</option>
                  <option value="print-mail">Print &amp; mail</option>
                </select>
              </div>
            </div>

            {!emailAvail && (
              <div style={noteS}>
                No email on file — electronic delivery (NetTeller) isn&apos;t
                available for {name}. Documents will be{' '}
                {r.delivery === 'print-mail' ? 'printed and mailed' : 'printed and hand-delivered'}.
                {required && r.mode === 'remote' && (
                  <> <strong>Remote signing needs an email address — add one on the People step or choose In person.</strong></>
                )}
              </div>
            )}
          </div>
        );
      })}
    </ModalShell>
  );
}

export default ConfigureSigningSessionModal;
